import { makeAutoObservable } from 'mobx'
import stripePrices from './../../constants/stripePrices.constants'
import { setGroupEandO } from './../../shared/utilities/setGroupEandO.function'
import AdminApi from './../../api/admin-api/admin-api'
import UserMetaService from './../../shared/services/UserMeta.service'
import UserCarrierService from './../../shared/services/UserCarrier.service'
import CarrierService from './../../shared/services/Carrier.service'
import ContentService from './../../shared/services/Content.service'
import UserProfileService from './../../shared/services/UserProfile.service'
import StripeCustomerService from './../../shared/services/StripeCustomer.service'
import StripeSubscriptionService from './../../shared/services/StripeSubscription.service'
import UserUserTypesService from './../../shared/services/UserUserTypes.service'
import { getIp } from './../../shared/utilities/getIp.function'

const GRACE_PERIOD_DAYS = 1

const notifyBrokerSupport = async (params) => {
  // old admin api 'downgradeToAffiliate' has been updated
  // to downgrade the agents account & send a notification
  // email.  The notification is CC  to broker support &
  // the agents upline.
  return await AdminApi.downgradeToAffiliate(params)
}

const cancelSubscription = async (subscriptionId) => {
  if (!subscriptionId)
    return Promise.reject(
      'Invalid subscriptionId argument supplied to cancelSubscription().'
    )

  let subscription
  try {
    subscription = await StripeSubscriptionService.delete(subscriptionId)
  } catch (ex) {
    return Promise.reject(ex)
  }

  fetchSubscription(subscriptionId)
  subscription = subscription ? subscription : undefined
  return subscription
}

const acceptTerms = async () => {
  const User = await UserProfileService.instance()
  if (User) {
    try {
      return await User.signTerms({
        term_slugs: [
          'sig-agent-terms',
          'sig-producer-eo',
          'non-captive-non-solicit',
          'subscriber-agreement',
        ],
        accepted_ip: await getIp(),
      })
    } catch (ex) {}
  }

  return false
}

const fetchDowngradeContent = async (downgradeTo) => {
  let contentId = null
  if (`${downgradeTo}`.trim().toLowerCase() === 'associate-agent')
    contentId = 66
  if (`${downgradeTo}`.trim().toLowerCase() === 'affiliate-agent')
    contentId = 67

  if (contentId > 0) return await ContentService.getPageById(contentId)
}

const fetchLastUserTypeHistory = async (userId) => {
  return (
    await UserUserTypesService.search({
      search: { user_id: userId },
      pagination: false,
      orderBy: { changed_at: 'DESC' },
    })
  )?.models.shift()
}

const fetchCarriersByUserId = async (userId) => {
  let carriers = []
  try {
    carriers = await UserCarrierService.search({
      pagination: false,
      search: { user_id: userId },
    })
  } catch (ex) {}

  carriers =
    carriers && carriers?.models ? carriers.models.map((c) => c.carrier_id) : []

  if (carriers.length)
    return (
      (
        await CarrierService.search({
          pagination: false,
          search: { id: carriers },
        })
      )?.models || []
    )

  return []
}

const fetchCustomer = async (customerId) => {
  if (!customerId)
    return Promise.reject(
      'Invalid customerId argument supplied to fetchCustomer().'
    )

  let customer
  try {
    customer = await StripeCustomerService.get(customerId, {
      expand: ['customer', 'paymentMethod'],
    })
  } catch (ex) {
    return Promise.reject(ex)
  }

  customer = customer ? customer : undefined

  if (customer) {
    console.log(' customer > ', customer.expands)
  }

  return customer
}

async function createStripeCustomer(User) {
  // Why was this in old logic?
  // let name = [User?.get('u_fname'), User?.get('u_lname')].join(' ').trim(),
  //     email = User?.get('u_email'),
  //     phone = User?.get('u_phone'),
  let subClass = 'sig_agent',
    customer

  try {
    customer = await StripeCustomerService.createByUserId(User.id(), {
      subClass,
      customerIp: await getIp(),
    })
    customer = customer && customer.id ? fetchCustomer(customer.id) : customer
    customer = customer ? customer : undefined

    return customer
  } catch (ex) {
    console.error(
      'ERROR: Failed to create new stripe customer in AccountBillingInfo.',
      ex
    )
  }
}

async function fetchStripeCustomerId(userId, subClass) {
  if (!userId)
    return Promise.reject(
      'Invalid userId argument supplied to fetchStripeCustomerId().'
    )

  let customerId
  try {
    customerId = await StripeCustomerService.search({
      search: { user_id: userId, sub_class: subClass },
    })
    customerId =
      customerId && customerId?.models && customerId.models.length
        ? customerId.models.shift().id
        : null
  } catch (ex) {
    return Promise.reject(ex)
  }

  return customerId && customerId.length ? customerId : undefined
}

async function getStripeCustomer(User) {
  User = User ? User : this.User

  let customer,
    customerId =
      User?.id() && (await fetchStripeCustomerId(User.id(), 'sig_agent'))

  // If no Customer ID was found, create a customer.
  customer = customerId
    ? await fetchCustomer(customerId)
    : await createStripeCustomer(User)

  return customer
}

async function fetchSubscriptionMerchant(User) {
  // const User = await UserProfileService.instance(),
  const UserMeta = (
    (await User.meta(true).key('billing---subscription-source')) || []
  ).shift()

  return UserMeta && UserMeta?.get('meta_value')
    ? JSON.parse(UserMeta.get('meta_value'))
    : null
}

async function fetchSubscription(subscriptionId) {
  if (!subscriptionId)
    return Promise.reject(
      'Invalid subscriptionId argument supplied to fetchSubscription().'
    )

  let subscription
  try {
    subscription = await StripeSubscriptionService.get(subscriptionId, {
      expand: ['subscription'],
    })
  } catch (ex) {
    return Promise.reject(ex)
  }

  return subscription ? subscription : undefined
}

const fetchIsAdminFeeWaived = async (userId) => {
  let today = new Date().toISOString().slice(0, 10),
    res =
      (
        await UserMetaService.search({
          search: {
            user_id: userId,
            meta_key: `subscription---waive-admin-fee`,
          },
          orderBy: { created_at: 'DESC' },
        })
      )?.models || []

  return !!res.filter((r) => r.meta_key && r.meta_value.includes(today))
}

const determineIsOnGracePeriod = async (userId, subStatus) => {
  const lastUsertype = await fetchLastUserTypeHistory(userId)

  return GRACE_PERIOD_DAYS > 0
    ? [111, 133, 223, 224].includes(lastUsertype?.usertype_id) &&
      lastUsertype?.changed_at &&
      lastUsertype?.changed_by !== 'enrollment' &&
      subStatus !== 'inactive'
      ? new Date().getTime() / 1000 <
        new Date(lastUsertype?.changed_at).getTime() / 1000 +
          GRACE_PERIOD_DAYS * 86400
      : false
    : false
}

const determineDowngradeUsertype = async (userId) => {
  if (userId) {
    const carriers = await fetchCarriersByUserId(userId)
    return await fetchDowngradeContent(
      carriers.filter(
        (c) => !!(parseInt(c.c_required) + parseInt(c.c_preferred))
      ).length >= 5
        ? 'associate-agent'
        : 'affiliate-agent'
    )
  }
}

class SubscriptionsStore {
  User = null
  isAgent = false
  isNonSigAgent = false
  isSigAgentVarient = false
  customerId = null
  customer = null
  subscriptionId = null
  subscription = null
  canUpgrade = false
  subSource = false
  paymentMethods = []
  isAdminFeeWaived = false
  isOnGrace = false
  isModifying = null
  isFetching = null
  downgradeContent = {}

  constructor() {
    makeAutoObservable(this)
  }

  isActive = () => ['active'].includes(this.subscription?.sub_status)

  isInactive = () =>
    ['incomplete', 'incomplete_expired', 'trialing', 'unpaid'].includes(
      this.subscription?.sub_status
    )

  isCanceled = () => ['canceled'].includes(this.subscription?.sub_status)

  isPastDue = () => ['past_due'].includes(this.subscription?.sub_status)

  initUser = async (User) => {
    this.isFetching = true

    // Do not re-init if the user has not changed.
    if (parseInt(this.User?.id()) === parseInt(User?.id())) return

    // Fetch the customer by User instance.
    this.fetchCustomer((this.User = User))

    // Indicate the type of agent that is reviewing their options.
    this.isAgent = this.User.isA(['agent'])
    this.isNonSigAgent = this.User.isA([
      'affiliate-agent',
      'affiliate-leader',
      'associate-agent',
      'associate-leader',
    ])
    this.isSigAgentVarient = this.User.isA([
      'limited-to-contracting-signature-agent',
      'limited-to-training-signature-agent',
      'signature-agent-in-need-of-pic',
      'signature-agent-complete',
      'career-agent',
      'premier-agent',
    ])

    // Indicate if user is permitted to upgrade.
    this.canUpgrade = parseInt(this.User?.get('can_upgrade_signature')) === 1

    // Indicate if the $50 admin fee should be waived.
    this.isAdminFeeWaived = await fetchIsAdminFeeWaived(this.User.id())

    // Indicate if the agent is on Grace Period.
    // Grace period will waive the admin fee for agents (where applicable).
    this.isOnGrace = this.isAdminFeeWaived
      ? this.isAdminFeeWaived
      : await determineIsOnGracePeriod(this.User.id())

    // Grab the agent's past merchant data, if needed.
    this.subSource = await fetchSubscriptionMerchant(User)

    // Determine the messaging to be used for potential downgrade.
    // This is based on agent's carriers.
    this.downgradeContent = await determineDowngradeUsertype(this.User.id())

    this.isFetching = false
  }

  fetchCustomer = async () => {
    // Fetch the customer by User instance.
    this.customer = await getStripeCustomer(this.User)
    this.customerId = this.customer?.id || null

    // Identify default payment method.
    const defaultPaymentId =
      this.customer?.expands?.customer?.invoice_settings?.default_payment_method
    const paymentMethods = (this.customer?.expands?.paymentMethod || []).map(
      (pm) => ({ ...pm, default: pm.id === defaultPaymentId })
    )
    this.paymentMethods = paymentMethods

    // Fetch Customer Subscription.
    const sub = (
      (
        await StripeSubscriptionService.search({
          search: { customer_id: this.customerId },
          order_by: { started_at: 'DESC' },
        })
      )?.models || []
    ).shift()
    if (sub && sub.id) {
      this.subscription = await fetchSubscription(sub.id)
      if (this.isActive() || this.isPastDue()) this.subscriptionId = sub.id
    }

    return this.customerId
  }

  deletePaymentMethod = async (paymentMethodId) => {
    if (this.customerId && this.paymentMethods.length > 0) {
      try {
        await StripeCustomerService.deletePaymentMethod(this.customerId, {
          payment_method_id: paymentMethodId,
        })
      } catch (ex) {
        throw new Error(`${ex}`)
      }

      this.fetchCustomer()
    }
  }

  setDefaultPaymentMethod = async (paymentMethodId) => {
    if (this.customerId) {
      try {
        await StripeCustomerService.setDefaultPaymentMethod(this.customerId, {
          payment_method_id: paymentMethodId,
        })
      } catch (ex) {
        throw new Error(`${ex}`)
      }

      this.fetchCustomer()
    }
  }

  subscribeSigAgent = async () => {
    this.isModifying = true

    // this.setState({ modifying: true })
    // 1. creates subscription.
    // 2. updates usertype.
    // 3. notifies upline.
    let prices = this.isOnGrace
      ? { items: [stripePrices.recurring] }
      : { items: [stripePrices.recurring], add_items: [stripePrices.one_time] }

    let subscription
    try {
      subscription = await StripeSubscriptionService.store({
        customer: this.customerId,
        ...prices,
        subscribingIp: await getIp(),
      })
      if (subscription && subscription.id)
        this.subscription = await fetchSubscription(
          (this.subscriptionId = subscription.id)
        )
    } catch (ex) {
      this.isModifying = false
      return Promise.reject(ex)
    }

    acceptTerms()

    if (!UserProfileService.isAssumed())
      await UserProfileService.fetchAndStoreUserDetails()

    // Generate E&O policy upon upgrade.
    // (Policy wont be generate if already exists).
    setGroupEandO()
    fetchLastUserTypeHistory(this.User.id())

    this.isModifying = false
    return true
  }

  unsubscribeSigAgent = async (formData) => {
    // 1. cancels subscription.
    // 2. updates usertype.
    // 3. notifies upline.
    this.isModifying = true

    let msg
    if (
      !this.subscriptionId ||
      (this.subSource &&
        this.subSource?.merchant !== 'stripe' &&
        !this.subSource?.canceled_at)
    ) {
      msg = 'Broker Support has been notified to downgrade your account.'
      await notifyBrokerSupport({ userId: this.User.id(), ...formData })
    } else {
      msg = 'Your subscription has been canceled'
      if (this.isAgent && !this.isNonSigAgent)
        msg += ' and your account has been downgraded'

      await cancelSubscription(this.subscriptionId)
      await notifyBrokerSupport({
        userId: UserProfileService.getUserId(),
        ...formData,
      })
    }

    if (!UserProfileService.isAssumed())
      await UserProfileService.fetchAndStoreUserDetails()

    fetchLastUserTypeHistory(this.User.id())

    this.isModifying = false
    return msg
  }
}

export default new SubscriptionsStore()
